import { useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-solid-svg-icons/faExternalLinkAlt';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { acceptNewTermsAndConditions } from './api';
import { messages } from './messages';
import { configs } from './new-terms-configs';
import style from './styles.module.less';
import { useLocale } from '../../../intl/hooks';
import { getLink } from '../../../services/link-service';
import { LogService } from '../../../services/log-service';
import Global from '../../../store/global-selectors';
import ModalPortal from '../../modal-portal';

export const NewTermsModal = ({ isAdmin, pastDeadline, onClose, newTermsLabel }) => {
  const [checkedTerms, setCheckedTerms] = useAsyncSafeState(false);
  const [hasAcceptError, setAcceptError] = useAsyncSafeState(false);

  const { locale } = useLocale();
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const { terms, privacy } = configs;

  const acceptTerms = useCallback(async () => {
    try {
      await acceptNewTermsAndConditions(newTermsLabel);
      onClose();
    } catch (e) {
      setAcceptError(true);
      LogService.logError(new Error('Error setting seller terms and conditions acceptance', { cause: e }));
    }
  }, [newTermsLabel, onClose, setAcceptError]);

  const dismissError = useCallback(() => setAcceptError(false), [setAcceptError]);

  const onCloseCallback = useMemo(() => (pastDeadline ? noop : onClose), [onClose, pastDeadline]);

  if (!isAdmin && !pastDeadline) {
    return null;
  }

  const bodyText = isAdmin ? <FormattedMessage {...messages.modalBodyAdmin} /> : <FormattedMessage {...messages.modalBodyNonAdmin} />;

  return (
    <ModalPortal onClose={onCloseCallback}>
      <Modal size='small'>
        <ModalHeader preventClose={pastDeadline}>
          <FormattedMessage {...messages.modalTitle} />
        </ModalHeader>
        <ModalBody className={style['new-terms-modal-body']}>
          <h4 className={style['new-terms-modal-header']}>
            <FormattedMessage {...messages.modalHeader} />
          </h4>
          {hasAcceptError ? (
            <BannerMessageV2
              className={style['new-terms-modal-body-warning']}
              content={<FormattedMessage {...messages.acceptError} />}
              onClose={dismissError}
              status={BannerStatus.Error}
            />
          ) : null}
          {!isAdmin && (
            <BannerMessageV2
              className={style['new-terms-modal-body-warning']}
              content={<FormattedMessage {...messages.applicationBlocked} />}
              status={BannerStatus.Error}
            />
          )}
          <p className={style['new-terms-modal-body-text']}>{bodyText}</p>
          {isAdmin ? (
            <>
              <Checkbox
                checked={checkedTerms}
                className={style['new-terms-modal-check']}
                label={<FormattedMessage {...messages.modalBodyAccept} />}
                onChange={() => setCheckedTerms((val) => !val)}
              />
              {terms ? (
                <a className={style['settings-link']} href={getLink(terms, homeCountry, locale)} rel='noreferrer' target='_blank'>
                  <FormattedMessage {...messages.modalBodyTerms} />
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
              ) : null}
              {privacy ? (
                <a className={style['settings-link']} href={getLink(privacy, homeCountry, locale)} rel='noreferrer' target='_blank'>
                  <FormattedMessage {...messages.modalBodyPrivacy} />
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </a>
              ) : null}
            </>
          ) : null}
        </ModalBody>
        {isAdmin ? (
          <ModalFooter>
            {!pastDeadline && <ModalFooterCancelButton />}
            <Button disabled={!checkedTerms} onClick={acceptTerms} theme='primary'>
              <FormattedMessage {...messages.modalCtaButton} />
            </Button>
          </ModalFooter>
        ) : null}
      </Modal>
    </ModalPortal>
  );
};

NewTermsModal.propTypes = {
  isAdmin: PropTypes.bool.isRequired,
  pastDeadline: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  newTermsLabel: PropTypes.string.isRequired,
};
